// The shared enumerations, in a leaf module that imports nothing but Zod.
//
// They live here rather than in `index.ts` because the barrel re-exports feature modules
// (`board-config`, `categories`) that need these values at module-init time. `export *` is
// hoisted, so a feature module importing from `./index` is evaluated before the barrel's own
// body runs and every binding it wants is still in the temporal dead zone — which broke every
// consumer of `@hoffman-support/contracts` at import time. Feature modules import from
// `./enums`; only `index.ts` re-exports it. Do not move these back into the barrel.

import { z } from "zod";

// Entra app roles. The support-staff role is `staff`, never `agent` — "the agent" always
// means the triage process (architecture plan §6.1).
export const ROLES = ["requester", "staff", "admin", "leadership"] as const;
export const RoleSchema = z.enum(ROLES);
export type Role = z.infer<typeof RoleSchema>;

// Projected request status. `cancelled` (board Removed) and `closed` (board Completed) are
// never conflated; `withdrawn` is request-level and set by the requester (§9.4).
export const REQUEST_STATUSES = [
  "new",
  "in_progress",
  "waiting_on_user",
  "resolved",
  "closed",
  "cancelled",
  "withdrawn",
] as const;
export const RequestStatusSchema = z.enum(REQUEST_STATUSES);
export type RequestStatus = z.infer<typeof RequestStatusSchema>;

// Azure DevOps state categories — mapped by category, never by state name (§9.4).
export const STATE_CATEGORIES = [
  "Proposed",
  "InProgress",
  "Resolved",
  "Completed",
  "Removed",
] as const;
export const StateCategorySchema = z.enum(STATE_CATEGORIES);
export type StateCategory = z.infer<typeof StateCategorySchema>;

// Per-category agent autonomy. Every category starts at L1 (§8.12).
export const AUTONOMY_LEVELS = ["L0", "L1", "L2", "L3"] as const;
export const AutonomyLevelSchema = z.enum(AUTONOMY_LEVELS);
export type AutonomyLevel = z.infer<typeof AutonomyLevelSchema>;

// Request ↔ work-item link types: one primary per request, never a Task (§9.7, §8.7).
export const LINK_TYPES = ["primary", "contributing", "child_task"] as const;
export const LinkTypeSchema = z.enum(LINK_TYPES);
export type LinkType = z.infer<typeof LinkTypeSchema>;

// Access plane of the work a request needs (§8.5): public cloud and vendor portals versus VPN,
// Hoffman LAN, or an internal management address. `mixed` work is parented by the plane required
// to complete the outcome; `unknown` is a classification the agent could not make — a reason to
// stop, never a default. Epic-routing keys (cloud / internal / onboarding) are a different enum
// in board-config: `onboarding` is a rollup target, not a plane.
export const ACCESS_PLANES = ["cloud", "internal", "mixed", "unknown"] as const;
export const AccessPlaneSchema = z.enum(ACCESS_PLANES);
export type AccessPlane = z.infer<typeof AccessPlaneSchema>;
